import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface FallBackRouting {
  id: string;
  type: string;
  name: string;
  isDeleted: boolean;
  effectiveDate: Date;
  createdDate: Date;
  percentage0: number;
  percentage1: number;
  percentage2: number;
  percentage3: number;
  conditionStatuses: ConditionValueType[];
  routingPlanId: string;
  provider1Id: string;
  provider2Id: string;
  provider3Id: string;
  operatorId: number;
  originalProviderId: string;
  updatedBy?: string;
  providerId: string;
  createdBy: string;
  activeTimer:number;
}
export interface FallBackRoutingHistory {
  id: string;
  snapshot: FallBackRouting & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}

export interface FallBackRoutingHistoryDiff {
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  snapshot: FallBackRouting & { timestamp: string };
  diff?: Record<string, string>;
}

export interface ConditionValueType {
  conditionType: "EQUAL" | string;
  status: string | null;
  commandStatus?: number | null;
  dlrErrorCode?: string | null;
  filterType?: string;
}

export const fetchSmppStatusCodes = async (): Promise<Record<string, string>> => {
  try {
    const response = await axiosInstance.get("/smpp/status-codes");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "An error occurred while fetching SMPP status codes."
    );
  }
};

export const exportFallBackRouting = async (
  params: Params<FallBackRouting> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/fall-back-routing/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "FallBackRouting.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching FallBackRouting :",
      error.response?.data?.message || error.message || error
    );
    throw error;
  }
};

export async function fetchFallBackRoutings(
  params: Params<FallBackRouting>
): Promise<IPageable<FallBackRouting>> {
  try {
    const response = await axiosInstance.get("/fall-back-routing", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}

export async function fetchFallBackRouting(
  id: FallBackRouting["id"]
): Promise<FallBackRouting> {
  try {
    const response = await axiosInstance.get(`/fall-back-routing/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting routing-plan/${id}.`
    );
  }
}

export const addFallBackRoutingApi = async (data: Partial<FallBackRouting>) => {
  try {
    const response = await axiosInstance.post("/fall-back-routing", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the FallBack Routing."
    );
  }
};
export const FallBackRoutingBulkApi = async (
  data: Partial<FallBackRouting>[]
) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.put("/fall-back-routing", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the FallBack Routing."
    );
  }
};

export const FallBackRoutingCreateAllApi = async (
  data: Partial<FallBackRouting>[]
) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.post("/fall-back-routing/all", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the FallBack Routing."
    );
  }
};

export const editFallBackRoutingApi = async (
  id: string,
  data: Partial<FallBackRouting>
) => {
  try {
    const response = await axiosInstance.put(`/fall-back-routing/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the FallBack Routing."
    );
  }
};

export const getFallBackRoutingById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/fall-back-routing/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the FallBack Routing."
    );
  }
};

export const deleteFallBackRoutingApi = async (
  ids: string[]
): Promise<void> => {
  try {
    await axiosInstance.delete(`/fall-back-routing`, { data: ids });
    console.log(`FallBack Routing with ID ${ids} deleted successfully.`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the FallBack Routing."
    );
  }
};

export const useFallBackRoutingQsuery = (
  params: Params<FallBackRouting>
): UseQueryResult<IPageable<FallBackRouting>, Error> => {
    const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["fallBackRouting", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchFallBackRoutings(effectiveParams),
  });
};

export async function fetchFallBackRoutingHistory(
  id: FallBackRouting["id"],
  params: Params<FallBackRoutingHistory> = {}
): Promise<IPageable<FallBackRouting>> {
  try {
    const response = await axiosInstance.get(`/instructions/history`, {
      params: { ...params, mainId: id, sort: "historyTimestamp,desc" },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the FallBack Routing history for ID ${id}.`
    );
  }
}
